import {SlButton, SlDialog, SlRadio, SlRadioGroup} from '@shoelace-style/shoelace/shoelace';
import {notNull} from '../../../../../../library/clxProductNet/assets/js/modules/common/assert';
import AnalyticsTracker from '../../../../../../library/clxProductNet/assets/js/modules/common/tracking/analyticsTracker';
import MicroConversionTracker from '../../../../../../library/clxProductNet/assets/js/modules/common/tracking/microConversionTracker';
import {getLocale} from '../../common/locale';
import {EventCategoryEnum} from '../../common/trackingFrontend/Model/EventCategoryEnum';
import {EventProductCustomizationEnum} from '../../common/trackingFrontend/Model/EventProductCustomizationEnum';
import {MicroConversionTrackingEventsEnum} from '../../common/trackingMicroconversion/Model/EventProductCustomizationEnum';
import ProductCustomizationConfigModel from '../Model/productCustomizationConfigModel';
import ProductCustomizationTrackingModel from '../Model/productCustomizationTrackingModel';
import ProductCustomizationOrder from '../productCustomizationOrder';
import CustomizationPricingOverviewComponent from './customizationPricingOverviewComponent';

export default class ProductCustomizationComponent extends HTMLElement {
    private readonly microConversionTracker: MicroConversionTracker;

    private readonly html: HTMLElement;

    private closeCustomizerButton: HTMLDivElement | null;

    private nextButton: SlButton;

    private infoAreas: NodeListOf<HTMLDivElement>;

    private readonly config: ProductCustomizationConfigModel;

    private readonly _tracking: AnalyticsTracker;
    private refreshOverviewUI: CustomizationPricingOverviewComponent | null;

    constructor() {
        super();
        this.microConversionTracker = new MicroConversionTracker(`/${getLocale()}/product-customization/apiTrack/`);

        this.config = ProductCustomizationConfigModel.getInstance();

        this.html = document.querySelector('html')!;
        const restrictedAccessSlDialog = document.querySelector<SlDialog>('#restricted_access');

        this._tracking = new AnalyticsTracker();

        this.refreshOverviewUI = this.querySelector<CustomizationPricingOverviewComponent>('#pricing-overview');

        this.nextButton = notNull(this.querySelector<SlButton>('.actions sl-button.next'));

        this.closeCustomizerButton = this.querySelector<HTMLDivElement>('.close-overlay');
        this.infoAreas = this.querySelectorAll('.customize .info-box:not(.noclick)');
    }

    connectedCallback() {
        this.initCustomizationConfigFromDOM();
        this._attachOverlayEventListeners();
    }

    disconnectedCallback() {
        this._detachOverlayEventListeners();
    }

    private initCustomizationConfigFromDOM() {
        const activeColorElement = notNull(this.querySelector<HTMLDivElement>('.article-colors .item.active'));
        const color = parseInt(activeColorElement.dataset!.color!);

        let currentlySelectedColorSizes = this.querySelector<SlRadioGroup>('.artsize.color-' + color);
        // As Shoelace has not yet been initialised, getAttribute is used here
        const groesseid = parseInt(currentlySelectedColorSizes!.getAttribute('value')!);
        const currentlySelectedSize = currentlySelectedColorSizes?.querySelector<SlRadio>('sl-radio[value="' + groesseid + '"]');
        this.config.setColor(color, groesseid, parseInt(currentlySelectedSize!.dataset!.artgroesseid!));

        const amountInputs: NodeListOf<HTMLInputElement> = this.querySelectorAll('.customization_amount');
        const firstInput = amountInputs[0];
        if (firstInput.dataset?.sku && firstInput.dataset?.groesseid) {
            // As Shoelace has not yet been initialised, getAttribute is used here
            const value = firstInput.getAttribute('value');
            if (value === null) {
                throw new Error('Value of amount input is null. Initialization of ProductCustomizationConfigModel failed.');
            }
            this.config.setAmount(parseInt(value), parseInt(firstInput.dataset?.sku), parseInt(firstInput.dataset?.groesseid));
        }

        this.registerMountingPositions();
    }

    private handleInfoClick = (item: HTMLDivElement) => () => {
        item.classList.toggle('open');
    };

    private handleClickCloseButton = async () => {
        this._closeCustomizer();
    };

    private handleClickNextButton = async () => {
        const customizationOrder = new ProductCustomizationOrder(this.config);
        await customizationOrder._openCustomizationOrderForm();
    };

    private handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            this._closeCustomizer();
        }
    };

    private _attachOverlayEventListeners() {
        this.infoAreas.forEach((item: HTMLDivElement) => {
            item.addEventListener('click', this.handleInfoClick(item));
        });
        this.closeCustomizerButton!.addEventListener('click', this.handleClickCloseButton);
        this.nextButton.addEventListener('click', this.handleClickNextButton);
        document.addEventListener('keydown', this.handleEscapeKey);
    }
    private _detachOverlayEventListeners() {
        this.infoAreas.forEach((item: HTMLDivElement) => {
            item.removeEventListener('click', this.handleInfoClick(item));
        });
        this.closeCustomizerButton!.removeEventListener('click', this.handleClickCloseButton);
        this.nextButton.removeEventListener('click', this.handleClickNextButton);
        document.removeEventListener('keydown', this.handleEscapeKey);
    }

    _closeCustomizer() {
        notNull(document.querySelector<SlButton>('#customize_now')).focus();

        this.classList.remove('open');
        this.html.classList.remove('no-scroll');

        this._tracking.fireMatomoTrackingEvent(EventCategoryEnum.productCustomization, EventProductCustomizationEnum.closeCustomization);

        let trackingModel = new ProductCustomizationTrackingModel(this.config);
        this.microConversionTracker.trackWithBeacon(MicroConversionTrackingEventsEnum.customization_close, trackingModel.getAsJsonStringForTracking());
    }

    async updateCustomizationOverview(eventName: string): Promise<void> {
        await this.refreshOverviewUI!.refreshOverviewUIAndHandleErrors(this.config, eventName);
    }

    private registerMountingPositions() {
        const positions = this.querySelectorAll<HTMLDivElement>('.position-decoration');

        for (const position of positions) {
            const activeProcessElement = notNull(position.querySelector<HTMLDivElement>('.finishing-process .item.active'));
            const activeColorCountElement = notNull(position.querySelector<HTMLDivElement>('.finishing-colors .item.active'));

            const mountingPositionId = parseInt(position.dataset.posid || '0', 10);
            const vetechId = parseInt(activeProcessElement!.dataset.vetech || '0', 10);
            const amountOfColors = parseInt(activeColorCountElement.dataset.noc || '0', 10);

            const isActive = position.classList.contains('active');

            this.config.addDecorationConfigOption(mountingPositionId, vetechId, amountOfColors, isActive);
        }
    }

    public deactivateGotoCheckoutButton() {
        this.nextButton.disabled = true;
    }

    public activateGotoCheckoutButton() {
        this.nextButton.disabled = false;
    }
}
customElements.define('clx-customization-product-customization', ProductCustomizationComponent);
